/**
 * PlanMemberJourney — the plan holder's leg of a split-link request, on the
 * shared journey components (approved prototype: Main.dc.html).
 *
 *   Welcome → Your details → A few questions → Sign → (page) Done
 *
 * The plan holder is not the patient: they confirm and sign only the plan
 * member section. The question model, the demographic seeding, the signature
 * lines and the emit logic are the same ones the patient's leg uses (lib/
 * bridgeSchema, lib/bridgeQuestionnaire), so the submission reaches the Mac
 * in the shape it always has. The page still owns submitting, the error
 * screen and the ending; `PlanMemberDone` is that ending.
 */
import React, { useEffect, useMemo, useState } from "react";

import ProgressTimeline from "../../shared/ui/journey/ProgressTimeline";
import FactBlocks from "../../shared/ui/journey/FactBlocks";
import GapCarousel from "../../shared/ui/journey/GapCarousel";
import SignStep from "../../shared/ui/journey/SignStep";
import DoneScreen from "../../shared/ui/journey/DoneScreen";
import { J, ensureJourneyKeyframes, motionOk } from "../../shared/ui/journey/journeyTheme";
import type { JourneyStep } from "../../shared/lib/journeySteps";
import { formRefusalCodeOf, formRefusalOf, openSecretBox, type FormRefusalCode } from "../../lib/bridgeClient";
import buildQuestionnaire, { renderBackendQuestions } from "../../lib/bridgeQuestionnaire";
import { loadFieldsSchema, seedSchemaDemographics, signatureLinesFrom, type ParsedFieldsSchema } from "../../lib/bridgeSchema";
import { identifierTargets, routeCardCandidatesToQuestions, splitKnownAndToAnswer } from "../../lib/questionnaireSections";
import InsuranceCardCapture from "./InsuranceCardCapture";
import ParseoLogo from "../../shared/ui/ParseoLogo";
import signerAudiencesFor from "../../lib/signerAudience";
import { deriveInitialPlanMemberSignatureName } from "../../lib/signatureName";
import { typedSignatureToPng } from "../../lib/typedSignature";
import {
  buildPlanMemberFieldValues,
  choiceOptions,
  expiryDays,
  expiryLabel,
  fromGapValue,
  gapGroups,
  isChoiceWidget,
  isoLocalDate,
  knownFactGroups,
  missingRequired,
  normalizeTyped,
  toGapQuestion,
  toGapValue,
  valueOf,
  type Answers,
  type KnownFactRow,
} from "../../lib/planMemberJourney";
import { PLAN_MEMBER_ATTESTATION_TEXT } from "./signingStatements";
import type { BridgeQuestion, PatientProfile } from "../../types/shared";

export const PLAN_MEMBER_STEPS: JourneyStep[] = [
  { key: "details", label: "Your details" },
  { key: "questions", label: "Questions" },
  { key: "sign", label: "Sign" },
  { key: "done", label: "Done" },
];

type Screen = "welcome" | "details" | "questions" | "sign";

export interface PlanMemberSignature {
  name: string;
  font: string;
  image_b64: string | null;
  source: string;
}

interface PlanMemberJourneyProps {
  session: Record<string, unknown> & {
    sessionToken?: string;
    assignmentId?: string;
    symmetricKeyB64?: string;
  };
  initialProfile: PatientProfile | Record<string, any> | null;
  defaultSignatureName: string;
  defaultSignatureFont: string;
  /** The page's submit: seals and sends, then moves to its own SUBMITTING /
   *  DONE / ERROR stages. */
  onSubmit: (fieldValues: Record<string, string>, signature: PlanMemberSignature) => void;
  /** The bridge refused the form for its fee: for the plan member that is
   *  409 `request_closed` (the patient closed the request; ruling
   *  2026-10-14). The page shows "This request was closed." and never why. */
  onRefused?: (code: FormRefusalCode) => void;
}

const h1: React.CSSProperties = { margin: "0 0 4px", fontSize: 24, lineHeight: 1.2, fontWeight: 700, color: J.ink };
const subtitle: React.CSSProperties = { fontSize: 14, color: J.muted };
const primaryButton: React.CSSProperties = {
  width: "100%",
  height: 52,
  border: 0,
  borderRadius: J.radiusButton,
  background: J.ink,
  color: J.white,
  fontSize: 16,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
};

const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/** The phone-width column every plan-holder screen sits in, with the
 *  timeline pinned above it. */
export function JourneyFrame(props: {
  currentKey: string | null;
  /** The rail's steps. Defaults to the plan holder's; the patient's leg
   *  passes its own (`patientSteps` in shared/lib/journeySteps). */
  steps?: JourneyStep[];
  doneAll?: boolean;
  showTimeline?: boolean;
  /** The ending renders outside RoleBannerWrap, so it paints the whole page
   *  itself; inside the wrap the banners already sit on a full-height page. */
  fullPage?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ background: J.paper, color: J.ink, minHeight: props.fullPage ? "100vh" : undefined }}>
      {/* The signature preview's script face. BridgeFieldsForm loads it the
          same way for the patient's leg; without it the name renders in serif
          (seen on the first headless walk of this page). */}
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Dancing+Script&display=swap" />
      <div style={{ maxWidth: 480, margin: "0 auto", display: "flex", flexDirection: "column", minHeight: "min(760px, 92vh)" }}>
        {props.showTimeline !== false ? (
          <div style={{ position: "sticky", top: 0, zIndex: 2, background: J.paper, padding: "18px 20px 10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <ParseoLogo size={26} />
              <span style={{ fontWeight: 700, fontSize: 17 }}>Parseo Health</span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: J.muted, background: J.white, border: `1px solid ${J.line}`, borderRadius: 999, padding: "3px 9px" }}>
                Secure link
              </span>
            </div>
            <ProgressTimeline steps={props.steps || PLAN_MEMBER_STEPS} currentKey={props.currentKey} doneAll={props.doneAll} />
          </div>
        ) : null}
        <div style={{ flex: "1 1 0", padding: "6px 20px 24px", display: "flex", flexDirection: "column" }}>
          {props.children}
        </div>
      </div>
    </div>
  );
}

/** The plan holder's ending (and the physician's own preview of any link). */
export function PlanMemberDone({ selfTest, showTimeline = true }: { selfTest: boolean; showTimeline?: boolean }) {
  return (
    <JourneyFrame currentKey="done" doneAll showTimeline={showTimeline} fullPage>
      {selfTest ? (
        // The bridge sync acknowledges a self-test without writing anything,
        // so say so rather than thanking a "plan member" who is really the
        // doctor checking the page.
        <DoneScreen
          title="Test complete — nothing was saved."
          body="This was your own preview of the link. Nothing you entered was written to the form, and nobody was notified."
        />
      ) : (
        // Their part is done; the rest of the request is the patient's, so
        // no tracker and no promise of updates — the decision is the
        // patient's news to receive, not theirs.
        <DoneScreen
          title="Submitted. Thank you."
          body="Sent to the patient's doctor's office."
          steps={[
            { label: "Details checked", when: "Today" },
            { label: "Questions answered", when: "Today" },
            { label: "Signed", when: "Just now" },
            { label: "Sent to the doctor's office", when: "Just now" },
          ]}
          footer="You can close this page."
        />
      )}
    </JourneyFrame>
  );
}

export default function PlanMemberJourney({
  session,
  initialProfile,
  defaultSignatureName,
  defaultSignatureFont,
  onSubmit,
  onRefused,
}: PlanMemberJourneyProps) {
  const [screen, setScreen] = useState<Screen>("welcome");
  const [schema, setSchema] = useState<ParsedFieldsSchema | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  // Required questions the carousel was finished without; shown again as a
  // list. Frozen when set, so a card does not vanish while it is answered.
  const [requiredKeys, setRequiredKeys] = useState<string[] | null>(null);
  // The plan holder's own name, or nothing — never the patient's first name
  // the preload blob carries for the banner (imp_797e5495).
  const [signatureName, setSignatureName] = useState(() =>
    deriveInitialPlanMemberSignatureName(defaultSignatureName, initialProfile as PatientProfile | null)
  );
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Keyframes go in before the first paint so the first screen's fade-up
  // does not flash.
  const [animate] = useState(() => {
    ensureJourneyKeyframes();
    return motionOk();
  });

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;
    (async () => {
      try {
        const parsed = (await loadFieldsSchema(session)) || {
          fields: [], schemaVersion: 1, readerSubjects: ["patient", "plan_member"], questions: null, signatureField: null,
        };
        seedSchemaDemographics(parsed.fields, initialProfile, parsed.readerSubjects);
        if (cancelled) return;
        setSchema(parsed);
      } catch (err) {
        const refusal = formRefusalCodeOf(err);
        if (refusal && onRefused) {
          if (!cancelled) onRefused(refusal);
          return;
        }
        console.error("plan member schema load failed:", err);
        if (!cancelled) setLoadError((err as Error).message || String(err));
        return;
      }
      // The form itself, for "See the form" — best effort; the leg works
      // without it.
      try {
        if (!session.symmetricKeyB64) return;
        const resp = await fetch(
          `/api/bridge/assignments/${encodeURIComponent(session.assignmentId as string)}/pdf`,
          { method: "GET", headers: { "X-Patient-Token": session.sessionToken as string }, credentials: "omit" }
        );
        if (!resp.ok) {
          const refusal = await formRefusalOf(resp);
          if (refusal && onRefused && !cancelled) onRefused(refusal);
          return;
        }
        if (cancelled) return;
        const body = await resp.json();
        if (!body.encrypted_b64 || cancelled) return;
        const bytes = openSecretBox(body.encrypted_b64, session.symmetricKeyB64);
        blobUrl = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "application/pdf" }));
        setPdfUrl(blobUrl);
      } catch (pdfErr) {
        console.warn("plan member form preview unavailable:", pdfErr);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // The session and preloaded profile are fixed for the life of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fields = useMemo(() => (schema ? schema.fields : []), [schema]);
  const audience = useMemo(() => signerAudiencesFor(session.role as string | undefined), [session]);
  const hasBackendQuestions = !!schema && Array.isArray(schema.questions) && schema.questions.length > 0;
  const built = useMemo(
    () =>
      hasBackendQuestions
        // keepAnswered: the known facts are shown to confirm, not set aside.
        ? renderBackendQuestions(schema!.questions as BridgeQuestion[], { fields, answers, keepAnswered: true })
        : buildQuestionnaire(fields, { audience, answers }),
    [hasBackendQuestions, schema, fields, answers, audience]
  );
  const { known, toAnswer } = useMemo(() => splitKnownAndToAnswer(built.questions), [built]);
  const groups = useMemo(() => knownFactGroups(known, fields, answers), [known, fields, answers]);
  const gapQuestions = useMemo(() => toAnswer.map(toGapQuestion), [toAnswer]);
  const gapCards = useMemo(() => gapGroups(toAnswer), [toAnswer]);
  const gapAnswers = useMemo(() => {
    const out: Record<string, string> = {};
    for (const q of toAnswer) out[String(q.target)] = toGapValue(q, answers[String(q.target)]);
    return out;
  }, [toAnswer, answers]);
  const signatureLines = useMemo(
    () => (schema ? signatureLinesFrom(schema.fields, schema.schemaVersion, schema.signatureField) : []),
    [schema]
  );

  const hasKnown = known.length > 0;
  const hasGaps = toAnswer.length > 0;
  // The benefits-card photo reader, offered whenever a plan identifier is
  // still to be answered (Michael: "we used to have a place for them to
  // upload the card and we read it"). Same routing as the patient's leg.
  const idTargets = useMemo(() => identifierTargets(built.questions), [built]);
  const needsCard = [idTargets.plan, idTargets.person].some(
    (t) => !!t && toAnswer.some((q) => String(q.target) === t) && !answers[t]
  );
  const applyCardCandidates = (candidates: any[]): number => {
    const current: Record<string, unknown> = {};
    for (const t of [idTargets.plan, idTargets.person]) if (t) current[t] = answers[t];
    const patch = routeCardCandidatesToQuestions(candidates, idTargets, current);
    const n = Object.keys(patch).length;
    if (n) setAnswers((p) => ({ ...p, ...patch }));
    return n;
  };

  const setAnswer = (target: string, value: Answers[string]) => setAnswers((p) => ({ ...p, [target]: value }));
  const go = (s: Screen) => {
    setScreen(s);
    try {
      window.scrollTo(0, 0);
    } catch {
      /* scrolling is a courtesy */
    }
  };
  const afterWelcome = () => go(hasKnown ? "details" : hasGaps ? "questions" : "sign");
  const afterDetails = () => go(hasGaps ? "questions" : "sign");
  const beforeQuestions = () => go(hasKnown ? "details" : "welcome");
  const finishQuestions = () => {
    const missing = missingRequired(toAnswer, answers);
    if (missing.length > 0) {
      setRequiredKeys(missing.map((q) => String(q.target)));
      return;
    }
    setRequiredKeys(null);
    go("sign");
  };

  function handleSubmit() {
    if (submitting) return;
    setSubmitting(true);
    // Typed answers go out in the shape the patient's masked inputs store.
    const normalized: Answers = { ...answers };
    for (const q of built.questions) {
      const k = String(q.target);
      const v = normalized[k];
      if (!isChoiceWidget(q) && typeof v === "string") normalized[k] = normalizeTyped(q, v);
    }
    const name = signatureName.trim();
    const fieldValues = buildPlanMemberFieldValues({
      questions: built.questions,
      hidden: built.hidden,
      answers: normalized,
      schemaFields: fields,
      signatureLines,
      signatureName: name,
      today: isoLocalDate(),
    });
    onSubmit(fieldValues, {
      name,
      font: defaultSignatureFont,
      image_b64: typedSignatureToPng(name, defaultSignatureFont),
      source: "patient_typed",
    });
  }

  const currentKey = screen === "welcome" ? null : screen;
  const fade: React.CSSProperties = animate ? { animation: "pj-fadeUp .38s ease both" } : {};
  const notReady =
    loadError ? (
      <p style={{ color: J.body, fontSize: 15, lineHeight: 1.5 }}>
        {"We couldn't load the form. Please ask the patient's doctor's office for a fresh link."}
      </p>
    ) : !schema ? (
      <p style={{ color: J.muted, fontSize: 15 }}>Getting the form ready…</p>
    ) : null;

  let body: React.ReactNode;
  if (screen === "welcome") {
    const days = expiryDays(session);
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 18 }}>
        <div style={{ fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", color: J.teal, fontWeight: 600 }}>
          Insurance coverage request
        </div>
        <h1 style={{ margin: 0, fontSize: 30, lineHeight: 1.15, fontWeight: 700, color: J.ink }}>Your signature is needed.</h1>
        <p style={{ margin: 0, fontSize: 16, lineHeight: 1.5, color: J.body }}>
          {"A coverage request has been made under your benefits plan, and the insurer needs the plan member's signature on it."}
        </p>
        {/* Right under the sentence, not pinned to the bottom: on a tall
            screen the pinned button sat a page away (Michael, 2026-10-09). */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 10 }}>
          <button type="button" onClick={afterWelcome} style={primaryButton}>
            Start <ArrowRight />
          </button>
          {days !== null ? (
            <div style={{ textAlign: "center", fontSize: 12, color: J.muted }}>{expiryLabel(days)}</div>
          ) : null}
        </div>
      </div>
    );
  } else if (notReady) {
    body = <div style={{ paddingTop: 18 }}>{notReady}</div>;
  } else if (screen === "details") {
    const factGroups = groups
      .filter((g) => g.rows.length > 0)
      .map((g) => ({ key: g.key, title: g.title, accent: g.accent, rows: g.rows.map(({ key, label, value }) => ({ key, label, value })) }));
    const choiceRows = groups.flatMap((g) => g.choices);
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <h1 style={h1}>Your details</h1>
          <div style={subtitle}>Tap Edit to change anything.</div>
        </div>
        <FactBlocks
          groups={factGroups}
          onChange={(rowKey, value) => {
            const q = known.find((x) => String(x.target) === rowKey);
            setAnswer(rowKey, q ? normalizeTyped(q, value) : value);
          }}
        />
        {choiceRows.length > 0 ? (
          <ChoiceFacts rows={choiceRows} answers={answers} onPick={setAnswer} />
        ) : null}
        <div style={{ paddingTop: 6 }}>
          <button type="button" onClick={afterDetails} style={primaryButton}>
            Looks right
          </button>
        </div>
      </div>
    );
  } else if (screen === "questions") {
    const requiredOnly = requiredKeys
      ? gapQuestions.filter((q) => requiredKeys.includes(q.key))
      : null;
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: 14, flex: "1 1 0" }}>
        <div>
          <h1 style={h1}>A few questions</h1>
          <div style={subtitle}>
            {requiredOnly ? "These are needed before you can sign." : "Skip what doesn't apply."}
          </div>
        </div>
        {requiredOnly ? (
          <GapCarousel
            key="required"
            mode="list"
            questions={requiredOnly}
            answers={gapAnswers}
            onAnswer={(key, raw) => {
              const q = toAnswer.find((x) => String(x.target) === key);
              if (q) setAnswer(key, fromGapValue(q, raw));
            }}
            onComplete={finishQuestions}
            continueLabel="Continue to sign"
          />
        ) : (
          <>
          {needsCard ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={subtitle}>Have your benefits card handy? A photo fills in the plan numbers.</div>
              <InsuranceCardCapture onExtracted={applyCardCandidates} />
            </div>
          ) : null}
          <GapCarousel
            key="carousel"
            mode="carousel"
            questions={gapQuestions}
            groups={gapCards}
            answers={gapAnswers}
            onAnswer={(key, raw) => {
              const q = toAnswer.find((x) => String(x.target) === key);
              if (q) setAnswer(key, fromGapValue(q, raw));
            }}
            onComplete={finishQuestions}
            onBack={beforeQuestions}
            continueLabel="Continue to sign"
          />
          </>
        )}
      </div>
    );
  } else {
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: 14, flex: "1 1 0" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <h1 style={h1}>Sign</h1>
          {pdfUrl ? (
            <button
              type="button"
              onClick={() => window.open(pdfUrl, "_blank", "noopener")}
              style={{
                background: "transparent",
                border: 0,
                padding: "13px 4px",
                margin: "-13px 0",
                color: J.teal,
                textDecoration: "underline",
                fontSize: 14,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              See the form
            </button>
          ) : null}
        </div>
        <SignStep
          name={signatureName}
          onNameChange={setSignatureName}
          attested={attested}
          onAttestedChange={setAttested}
          statement={PLAN_MEMBER_ATTESTATION_TEXT}
          summary="Details checked."
          onReview={hasKnown ? () => go("details") : hasGaps ? () => go("questions") : undefined}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      </div>
    );
  }

  return (
    <JourneyFrame currentKey={currentKey}>
      <div key={screen} style={{ display: "flex", flexDirection: "column", flex: "1 1 0", ...fade }}>
        {body}
      </div>
    </JourneyFrame>
  );
}

/**
 * Known facts answered by a pick (sex, language…). FactBlocks edits text, so
 * these get the card look with the options inline: the current one filled,
 * one tap to change it.
 */
function ChoiceFacts({
  rows,
  answers,
  onPick,
}: {
  rows: KnownFactRow[];
  answers: Answers;
  onPick: (target: string, value: Answers[string]) => void;
}) {
  return (
    <section
      aria-label="Also on file"
      style={{ background: J.white, borderRadius: J.radiusCard, padding: "14px 16px 6px", boxShadow: J.shadow }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: J.muted }} />
        <div style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: J.teal, fontWeight: 700 }}>
          Also on file
        </div>
      </div>
      {rows.map((row) => {
        const q = row.question;
        const current = toGapValue(q, valueOf(q, answers));
        const labelId = `pm-choice-${row.key}`;
        return (
          <div key={row.key} style={{ padding: "9px 0 11px", borderTop: `1px solid ${J.hairline}` }}>
            <div id={labelId} style={{ fontSize: 12, color: J.muted, marginBottom: 6 }}>
              {row.label}
            </div>
            <div role="group" aria-labelledby={labelId} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {choiceOptions(q).map((opt) => {
                const on = current === opt.label;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => onPick(row.key, fromGapValue(q, opt.label))}
                    style={{
                      minHeight: 44,
                      padding: "0 14px",
                      borderRadius: 999,
                      border: `1.5px solid ${on ? J.ink : J.border}`,
                      background: on ? J.ink : J.white,
                      color: on ? J.white : J.ink,
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: "inherit",
                      cursor: "pointer",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </section>
  );
}
